/**
 * The engine's random number generator (SPEC.md §4.2).
 *
 * RNG state lives *inside* `GameState` and is cloned with it, so a cloned game continues
 * the same random sequence and replays are exact. This is SplitMix32: counter-based (the
 * state is a counter advanced by a fixed odd increment, then avalanche-mixed), 32-bit
 * integer math only, no BigInt — the same family as the PCG32 the spec names as an example.
 *
 * Only `determinize(state, player, rng)` (ticket 05) will take an RNG from outside.
 */

/** The whole generator: one 32-bit counter. Cloned by value. */
export interface RngState {
  /** The counter, kept as a uint32. */
  counter: number;
}

const GOLDEN_GAMMA = 0x9e3779b9;

export function createRngState(seed: number): RngState {
  return { counter: seed >>> 0 };
}

export function cloneRngState(rng: RngState): RngState {
  return { counter: rng.counter };
}

/** Next 32-bit unsigned integer; advances the counter. */
export function nextUint32(rng: RngState): number {
  rng.counter = (rng.counter + GOLDEN_GAMMA) >>> 0;
  let z = rng.counter;
  z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
  z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
  return (z ^ (z >>> 15)) >>> 0;
}

/**
 * Uniform integer in `[0, bound)`. Rejection-sampled, so the distribution is exactly
 * uniform and the number of RNG draws is itself part of the deterministic sequence.
 */
export function nextIntBelow(rng: RngState, bound: number): number {
  if (!Number.isInteger(bound) || bound <= 0) {
    throw new RangeError(`nextIntBelow needs a positive integer bound, got ${bound}`);
  }
  if (bound === 1) return 0;
  // Largest multiple of `bound` that fits in a uint32; draws at or above it are rejected.
  const limit = Math.floor(0x100000000 / bound) * bound;
  for (;;) {
    const draw = nextUint32(rng);
    if (draw < limit) return draw % bound;
  }
}

/** Fisher–Yates, in place, consuming the game's RNG. */
export function shuffleInPlace<T>(items: T[], rng: RngState): void {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = nextIntBelow(rng, i + 1);
    const a = items[i] as T;
    const b = items[j] as T;
    items[i] = b;
    items[j] = a;
  }
}
